export function projectRows(state, adapter) {
    const items = [];
    if (!state.caughtUp && state.liveCatchUp === 'inactive' && !state.liveTopRecovery) {
        items.push(...projectDataRows(state.lead.rows, state, adapter));
        items.push({ kind: 'buffer', id: 'b:up', direction: 'up' });
        items.push(...projectDataRows(state.rows, state, adapter));
    }
    else {
        // Lead rows sort before the ordinary window; the collections remain separate for fetching.
        items.push(...projectDataRows([...state.lead.rows, ...state.rows], state, adapter));
    }
    if (!state.reachedEnd)
        items.push({ kind: 'buffer', id: 'b:down', direction: 'down' });
    return items;
}
function projectDataRows(rows, state, adapter) {
    const items = [];
    let previousDate = '';
    for (const row of rows) {
        const key = adapter.keyOf(row);
        const date = adapter.calendarDateOf(row).toString();
        if (date !== previousDate)
            items.push({ kind: 'separator', id: `s:${key}`, date });
        items.push({
            kind: 'data',
            id: `r:${key}`,
            key,
            row,
            parity: state.parity.get(key) ?? 'odd',
            unmatchedAt: state.unmatchedAt.get(key) ?? null,
            fromPaging: state.pagedRowKeys.has(key),
        });
        previousDate = date;
    }
    return items;
}
export function measureLayout(items, measurement, config, pinAtLiveTop = false) {
    const rows = [];
    let top = 0;
    let topVisible = false;
    let bottomVisible = false;
    for (const item of items) {
        const fallback = item.kind === 'data'
            ? config.rowHeight
            : item.kind === 'separator'
                ? config.separatorHeight
                : config.bufferHeight;
        const height = measurement.heights.get(item.id) ?? fallback;
        const visible = top < measurement.scrollTop + measurement.viewportHeight && top + height > measurement.scrollTop;
        if (item.kind === 'data')
            rows.push({ id: item.id, key: item.key, top, height });
        if (item.kind === 'buffer' && visible) {
            if (item.direction === 'up')
                topVisible = true;
            else
                bottomVisible = true;
        }
        top += height;
    }
    return {
        rows,
        contentHeight: top,
        scrollTop: measurement.scrollTop,
        viewportHeight: measurement.viewportHeight,
        topVisible,
        bottomVisible,
        pinned: pinAtLiveTop && measurement.scrollTop <= config.pinnedTolerance,
    };
}
export function compensateScroll(before, after) {
    if (before.pinned)
        return 0;
    const clamp = (value) => Math.max(0, Math.min(value, Math.max(0, after.contentHeight - after.viewportHeight)));
    let anchorIndex = before.rows.findIndex((row) => row.top + row.height > before.scrollTop && row.top < before.scrollTop + before.viewportHeight);
    if (anchorIndex === -1) {
        // A viewport can sit wholly inside a paging placeholder. Anchor its nearest data row
        // outside the viewport so inserting a page moves the reader clear of that placeholder.
        anchorIndex = before.rows.findIndex((row) => row.top >= before.scrollTop + before.viewportHeight);
        if (anchorIndex === -1)
            anchorIndex = before.rows.length - 1;
    }
    if (anchorIndex === -1)
        return clamp(before.scrollTop);
    // A replacement inherits the reading slot. Preserving its own former offset can leave it
    // below the viewport and move the reader toward newer rows on each retention sweep.
    const anchorOffset = before.rows[anchorIndex].top - before.scrollTop;
    const positions = new Map(after.rows.map((row) => [row.key, row.top]));
    const candidates = [...before.rows.slice(anchorIndex), ...before.rows.slice(0, anchorIndex).reverse()];
    for (const row of candidates) {
        const position = positions.get(row.key);
        if (position !== undefined)
            return clamp(position - anchorOffset);
    }
    return clamp(before.scrollTop);
}
/** Contiguous data-only slice; mandatory visible + overscan rows override the soft target. */
export function retainedSlice(layout, config) {
    if (layout.viewportHeight <= 0)
        return null;
    const start = layout.rows.findIndex((row) => row.top + row.height > layout.scrollTop && row.top < layout.scrollTop + layout.viewportHeight);
    if (start === -1)
        return null;
    let end = start;
    while (end + 1 < layout.rows.length && layout.rows[end + 1].top < layout.scrollTop + layout.viewportHeight)
        end++;
    const mandatoryStart = Math.max(0, start - config.overscanRows);
    const mandatoryEnd = Math.min(layout.rows.length - 1, end + config.overscanRows);
    const count = Math.min(layout.rows.length, Math.max(config.retainedWindowRows, mandatoryEnd - mandatoryStart + 1));
    const spare = count - (mandatoryEnd - mandatoryStart + 1);
    const keepStart = Math.max(0, Math.min(mandatoryStart - Math.floor(spare / 2), layout.rows.length - count));
    return { start: keepStart, end: keepStart + count - 1 };
}
/** Lead-only projection for collection inspection; the mounted view uses the combined projectRows. */
export function projectLeadRows(state, adapter) {
    return projectDataRows(state.lead.rows, state, adapter);
}
//# sourceMappingURL=geometry.js.map